import type { AgendamentoNaLista, DiaDoCalendario } from '@baiocchi/shared'
import { cn } from '@baiocchi/ui'

import { formatarHora } from '../../utilitarios/formatar'

/** Faixa horária observada na TELA-08: 08h às 19h. */
export const HORA_INICIAL = 8
export const HORA_FINAL = 19

export interface PropriedadesGrade {
  dias: ReadonlyArray<DiaDoCalendario>
  /** `YYYY-MM-DD` do dia civil de hoje, para destacar a coluna. */
  hoje: string
  onSelecionarEvento: (agendamento: AgendamentoNaLista) => void
  /** Clique em espaço vazio: cria agendamento naquele dia/hora. */
  onSelecionarVago: (dia: string, hora: number) => void
  podeCriar: boolean
}

/**
 * Grade semanal / diária do calendário.
 *
 * Os dias vêm **já agrupados pelo servidor** por dia civil de São Paulo (§7):
 * agrupar no cliente por dia UTC jogaria o compromisso das 22h de terça para
 * quarta. A grade só posiciona.
 *
 * Não há arrastar-e-soltar: mover a data é **reagendar**, e reagendar exige motivo
 * e preserva o rastro de quantas vezes o cliente já desmarcou. Um arrasto que
 * salvasse em silêncio apagaria essa informação.
 */
export function GradeCalendario({
  dias,
  hoje,
  onSelecionarEvento,
  onSelecionarVago,
  podeCriar,
}: PropriedadesGrade) {
  const horas = Array.from({ length: HORA_FINAL - HORA_INICIAL + 1 }, (_, i) => HORA_INICIAL + i)

  return (
    <div className="overflow-x-auto">
      <div
        className="grid min-w-[44rem]"
        style={{ gridTemplateColumns: `4rem repeat(${dias.length}, minmax(8rem, 1fr))` }}
      >
        {/* Cabeçalho */}
        <div className="sticky left-0 z-10 border-b border-r border-borda bg-superficie-2" />
        {dias.map((dia) => {
          const ehHoje = dia.dia === hoje
          return (
            <div
              key={dia.dia}
              className={cn(
                'border-b border-r border-borda px-2 py-2 text-center last:border-r-0',
                ehHoje ? 'bg-marca-50' : 'bg-superficie-2',
              )}
            >
              <p
                className={cn(
                  'text-xs font-semibold uppercase',
                  ehHoje ? 'text-marca-700' : 'text-conteudo-suave',
                )}
              >
                {rotuloDoDia(dia.dia)}
              </p>
              <p className="text-[0.6875rem] text-conteudo-fraco">
                {dia.total} {dia.total === 1 ? 'compromisso' : 'compromissos'}
              </p>
            </div>
          )
        })}

        {/* Linha "dia inteiro" */}
        <div className="sticky left-0 z-10 border-b border-r border-borda bg-superficie-2 px-2 py-1.5 text-right text-[0.625rem] font-medium uppercase text-conteudo-fraco">
          dia
        </div>
        {dias.map((dia) => (
          <div
            key={`inteiro-${dia.dia}`}
            className={cn(
              'min-h-9 space-y-1 border-b border-r border-borda p-1 last:border-r-0',
              dia.dia === hoje && 'bg-marca-50/40',
            )}
          >
            {dia.agendamentos
              .filter((agendamento) => agendamento.diaInteiro)
              .map((agendamento) => (
                <Evento
                  key={agendamento.uuid}
                  agendamento={agendamento}
                  onSelecionar={onSelecionarEvento}
                />
              ))}
          </div>
        ))}

        {/* Faixas horárias */}
        {horas.map((hora) => (
          <FaixaHoraria
            key={hora}
            hora={hora}
            dias={dias}
            hoje={hoje}
            onSelecionarEvento={onSelecionarEvento}
            onSelecionarVago={onSelecionarVago}
            podeCriar={podeCriar}
          />
        ))}
      </div>

      <p className="border-t border-borda px-3 py-2 text-[0.6875rem] text-conteudo-fraco">
        Compromissos fora da faixa de {HORA_INICIAL}h às {HORA_FINAL}h aparecem na visão de lista.
      </p>
    </div>
  )
}

function FaixaHoraria({
  hora,
  dias,
  hoje,
  onSelecionarEvento,
  onSelecionarVago,
  podeCriar,
}: {
  hora: number
  dias: ReadonlyArray<DiaDoCalendario>
  hoje: string
  onSelecionarEvento: (agendamento: AgendamentoNaLista) => void
  onSelecionarVago: (dia: string, hora: number) => void
  podeCriar: boolean
}) {
  return (
    <>
      <div className="sticky left-0 z-10 border-b border-r border-borda bg-superficie-2 px-2 py-1 text-right text-[0.6875rem] tabular-nums text-conteudo-fraco">
        {String(hora).padStart(2, '0')}h
      </div>
      {dias.map((dia) => {
        const eventos = dia.agendamentos.filter(
          (agendamento) => !agendamento.diaInteiro && horaDe(agendamento.iniciaEm) === hora,
        )
        return (
          <div
            key={`${dia.dia}-${hora}`}
            className={cn(
              'relative min-h-12 border-b border-r border-borda p-1 last:border-r-0',
              dia.dia === hoje && 'bg-marca-50/40',
            )}
          >
            {podeCriar && eventos.length === 0 ? (
              <button
                type="button"
                onClick={() => onSelecionarVago(dia.dia, hora)}
                aria-label={`Criar agendamento em ${rotuloDoDia(dia.dia)} às ${hora}h`}
                className="group absolute inset-0 flex items-center justify-center text-conteudo-fraco opacity-0 transition-opacity hover:opacity-100"
              >
                <span className="text-xs font-medium">+ agendar</span>
              </button>
            ) : null}

            <div className="relative space-y-1">
              {eventos.map((agendamento) => (
                <Evento
                  key={agendamento.uuid}
                  agendamento={agendamento}
                  onSelecionar={onSelecionarEvento}
                />
              ))}
            </div>
          </div>
        )
      })}
    </>
  )
}

/**
 * Evento na grade. A cor vem do **cadastro do motivo** (TELA-12), não de uma
 * paleta da tela: é a legenda que a operação já conhece.
 *
 * A borda esquerda repete a cor em 3px e o rótulo traz o horário e o motivo — a
 * cor nunca é o único canal de informação.
 */
function Evento({
  agendamento,
  onSelecionar,
}: {
  agendamento: AgendamentoNaLista
  onSelecionar: (agendamento: AgendamentoNaLista) => void
}) {
  const cancelado =
    agendamento.status === 'CANCELADO' ||
    agendamento.status === 'NAO_COMPARECEU' ||
    agendamento.status === 'REAGENDADO'

  return (
    <button
      type="button"
      onClick={() => onSelecionar(agendamento)}
      title={`${agendamento.motivo.titulo} · ${agendamento.lead.clienteNome}`}
      className={cn(
        'relative z-10 block w-full overflow-hidden rounded border-l-[3px] bg-superficie px-1.5 py-1 text-left shadow-sm transition-shadow hover:shadow-md',
        cancelado && 'opacity-55',
        agendamento.vencido && 'ring-1 ring-perigo/50',
      )}
      style={{ borderLeftColor: agendamento.motivo.cor }}
    >
      <span className="block truncate text-[0.6875rem] font-semibold text-conteudo">
        {agendamento.diaInteiro ? 'Dia inteiro' : formatarHora(agendamento.iniciaEm)}{' '}
        {agendamento.lead.clienteNome}
      </span>
      <span className="block truncate text-[0.625rem] text-conteudo-fraco">
        {agendamento.motivo.titulo}
      </span>
      {cancelado ? (
        <span className="block truncate text-[0.625rem] font-medium text-perigo line-through">
          {agendamento.status === 'REAGENDADO' ? 'reagendado' : 'cancelado'}
        </span>
      ) : null}
    </button>
  )
}

function horaDe(iso: string): number {
  return Number(
    new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      hour12: false,
    }).format(new Date(iso)),
  )
}

function rotuloDoDia(dia: string): string {
  // `dia` é `YYYY-MM-DD` no fuso operacional; o meio-dia UTC evita que a
  // formatação escorregue para o dia anterior.
  const data = new Date(`${dia}T12:00:00Z`)
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(data)
}
